/**
 * The onboarding step registry.
 *
 * This is the single source of truth for the flow. Routing, progress, guards
 * and back-navigation are all derived from it — nothing about the sequence is
 * hardcoded anywhere else. Adding, removing or reordering a step is an edit to
 * this array and nothing more.
 *
 * Two properties make that possible:
 *
 *   `weight`  — how many screens this step actually presents. Most steps are 1.
 *               Goal sub-flows vary with the goals selected, so their weight is
 *               a function of flow state rather than a constant. Progress is a
 *               fraction of total weight, which is why a sub-flow spanning six
 *               screens moves the bar six times instead of sitting still.
 *
 *   `isReachable` — whether the flow may enter this step given what has been
 *               collected so far. The guard walks the registry rather than
 *               consulting a list of rules kept somewhere else, so a step can
 *               never become reachable by accident.
 *
 *   `appliesTo` — whether this journey includes the step at all. Distinct from
 *               reachability, which is about *now*: everything ahead of the
 *               member is unreachable but still part of their flow. Progress
 *               counts what applies, so the total is known from the first
 *               screen instead of growing as questions get answered.
 *
 *   `countsTowardProgress` - whether the bar numbers this step. Absent means
 *               yes. Distinct again from `appliesTo`: the confirmation is part
 *               of every journey and is not a step the member completes, so it
 *               is visited but never numbered.
 *
 * Steps whose screens are built in later tickets are registered here from the
 * start. Components are attached in `routes.jsx`, not here, so this module
 * stays free of JSX and testable on its own; a step with no component yet
 * renders a placeholder. That is what makes the sequence, the progress
 * denominator and the guards correct and testable before a screen exists.
 */

/**
 * @typedef {'welcome'|'dob'|'create_account'|'verify_email'|'goals'
 *   |'person_flow'|'profile'|'demographics'|'done'} StepId
 *
 * @typedef {object} FlowState
 * @property {'person'|'organization'|null} pathway
 * @property {boolean} existingAccount
 * @property {string|null} dob
 * @property {string|null} email
 * @property {boolean} emailVerified
 * @property {string[]} goals
 * @property {Record<string, unknown>} goalData
 * @property {Record<string, unknown>} profile
 * @property {Record<string, unknown>} demographics
 *
 * @typedef {object} StepDefinition
 * @property {StepId} id
 * @property {string} path            URL segment under /onboarding
 * @property {number|((s: FlowState) => number)} weight
 * @property {(s: FlowState) => boolean} isReachable
 * @property {boolean} [submitsOnArrival] Sends completion as soon as it renders
 * @property {boolean} [countsTowardProgress] Numbered by the bar; absent means yes
 * @property {string} [ticket]        Which ticket builds this screen
 */

/** Steps with a fixed single screen. */
const ONE_SCREEN = 1;

/**
 * The five goals, in the order they are *presented* on the goal step.
 *
 * These identifiers are the API contract (decision 6 in
 * `docs/onboarding-redesign-decisions.md`): no goals model exists on the
 * backend yet — ECH-110 landed with only the age gate — so these strings are
 * what it gets built against. Renaming one silently breaks a server that has
 * not been written. Labels and descriptions live in `src/locales/en.js`.
 *
 * Presentation order only: sub-flows run in `SUB_FLOW_ORDER`.
 */
export const GOAL_IDS = [
  "grow_career",
  "networking",
  "provide_mentorship",
  "receive_mentorship",
  "build_skills",
];

/** At least one goal is required to leave the goal step. */
export const MIN_GOALS = 1;

/**
 * The aviation taxonomy: 31 sectors, seven of which ask a follow-up.
 *
 * Every sector question in the onboarding flow asks from this list.
 *
 * ## Identifiers, not copy
 *
 * These are the contract, exactly as `GOAL_IDS` is. They are what lands in
 * `goalData` and what the backend will be built against, so the label has to be
 * free to change without orphaning stored answers, and it has to be
 * translatable. Display text lives in `src/locales/en.js`.
 *
 * ## Why the `vocabulary.member` prefix
 *
 * Bare slugs collide: `air_traffic_services` and `aircraft_maintenance` are
 * both sectors AND options under RCAF. Separate maps hide that today, but the
 * moment the two vocabularies share a table, an enum or a flattened i18n
 * namespace the values are indistinguishable — and a stored answer cannot say
 * which it meant.
 *
 * The prefix earns its keep twice: it is also the locale sub-path, so any id
 * resolves with a single `t(`onboarding.vocab.${id}`)` and no component has to
 * know which vocabulary it is holding.
 *
 * The old flow shows what happens otherwise: `ethnicity` stores display strings
 * and its arrays mix cases ("other" beside "Other"), and `interests` stores
 * free-typed titles matched case-insensitively into a catalog.
 *
 * ## Provenance
 *
 * Ported from the Figma export's `shared/AviationSectorPicker.tsx`, the only
 * complete statement of the list. Decision 7 fixes the count and which seven
 * carry a secondary; it also says the backend will own this eventually, which
 * is why callers go through `getSectorTaxonomy()` and never touch these arrays.
 *
 * The list itself comes from the client and will change as we learn from user
 * behaviour (Tony, 2026-08-25); the design governs its membership. Mirror it —
 * do not add, prune or rename a member here because it looks wrong from the
 * code, raise it against the design instead.
 *
 * Not modelled on the old flow's `getSubSpecialization`, which slices a flat
 * array by index with overlapping ranges. A keyed map cannot drift that way.
 */
const AVIATION_SECTORS = [
  "aviation_sectors.aerospace_and_manufacturing",
  "aviation_sectors.air_traffic_services",
  "aviation_sectors.aircraft_maintenance",
  "aviation_sectors.aircraft_manufacturing",
  "aviation_sectors.airworthiness_and_quality_assurance",
  "aviation_sectors.airport_ground_operations",
  "aviation_sectors.airport_operations",
  "aviation_sectors.aviation_consulting",
  "aviation_sectors.aviation_management_and_leadership",
  "aviation_sectors.aviation_non_profit_leadership",
  "aviation_sectors.aviation_safety",
  "aviation_sectors.aviation_sales_and_insurance",
  "aviation_sectors.aviation_security_and_screening",
  "aviation_sectors.business_aviation",
  "aviation_sectors.cargo_operations",
  "aviation_sectors.customer_service",
  "aviation_sectors.data_analytics",
  "aviation_sectors.drone_rpas_operations",
  "aviation_sectors.emerging_technologies",
  "aviation_sectors.engineering",
  "aviation_sectors.flight_attendant_cabin_crew",
  "aviation_sectors.flight_dispatch",
  "aviation_sectors.flight_operations",
  "aviation_sectors.human_resources",
  "aviation_sectors.information_technology",
  "aviation_sectors.pilot_aeroplane",
  "aviation_sectors.pilot_helicopter_rotary_wing",
  "aviation_sectors.professional_skills",
  "aviation_sectors.rcaf",
  "aviation_sectors.regulatory_and_government_aviation",
  "aviation_sectors.training_and_instruction",
];

/**
 * Sub-type option ids per sector, for a member looking FOR a mentor.
 *
 * Carries `not_sure_yet`, which the offering variant deliberately omits:
 * reasonable when describing what you want to learn, not what you can teach.
 */
const SECONDARY_SEEKING = {
  "aviation_sectors.pilot_aeroplane": [
    "aviation_subtypes.any",
    "aviation_subtypes.not_sure_yet",
    "aviation_subtypes.student_pilot",
    "aviation_subtypes.newly_licensed_pilot",
    "aviation_subtypes.airline_pilot",
    "aviation_subtypes.corporate_business_aviation_pilot",
    "aviation_subtypes.charter_pilot",
    "aviation_subtypes.medevac_pilot",
    "aviation_subtypes.flight_instructor",
    "aviation_subtypes.aerial_work_pilot",
    "aviation_subtypes.government_pilot",
    "aviation_subtypes.military_pilot",
    "aviation_subtypes.test_pilot",
    "aviation_subtypes.other",
  ],
  "aviation_sectors.pilot_helicopter_rotary_wing": [
    "aviation_subtypes.any",
    "aviation_subtypes.not_sure_yet",
    "aviation_subtypes.student_pilot",
    "aviation_subtypes.newly_licensed_pilot",
    "aviation_subtypes.ems_medevac",
    "aviation_subtypes.utility_and_resource_operations",
    "aviation_subtypes.firefighting",
    "aviation_subtypes.tourism_and_charter",
    "aviation_subtypes.offshore_operations",
    "aviation_subtypes.military_pilot",
    "aviation_subtypes.flight_instructor",
    "aviation_subtypes.other",
  ],
  "aviation_sectors.air_traffic_services": [
    "aviation_subtypes.any",
    "aviation_subtypes.not_sure_yet",
    "aviation_subtypes.air_traffic_control",
    "aviation_subtypes.flight_service_specialist_fss",
    "aviation_subtypes.flight_information_services",
  ],
  "aviation_sectors.aircraft_maintenance": [
    "aviation_subtypes.any",
    "aviation_subtypes.not_sure_yet",
    "aviation_subtypes.avionics",
    "aviation_subtypes.structures",
    "aviation_subtypes.quality_assurance",
    "aviation_subtypes.maintenance",
  ],
  "aviation_sectors.data_analytics": [
    "aviation_subtypes.any",
    "aviation_subtypes.not_sure_yet",
    "aviation_subtypes.flight_data_and_performance",
    "aviation_subtypes.safety_and_risk_analysis",
    "aviation_subtypes.airport_operations_analytics",
    "aviation_subtypes.maintenance_analytics",
    "aviation_subtypes.weather_and_forecasting",
    "aviation_subtypes.business_intelligence_and_reporting",
  ],
  "aviation_sectors.rcaf": [
    "aviation_subtypes.any",
    "aviation_subtypes.not_sure_yet",
    "aviation_subtypes.military_pilot",
    "aviation_subtypes.aerospace_control",
    "aviation_subtypes.air_operations",
    "aviation_subtypes.aerospace_engineering",
    "aviation_subtypes.aircraft_maintenance",
    "aviation_subtypes.air_traffic_services",
    "aviation_subtypes.intelligence_and_surveillance",
    "aviation_subtypes.leadership_and_officer_careers",
  ],
  "aviation_sectors.professional_skills": [
    "aviation_subtypes.any",
    "aviation_subtypes.not_sure_yet",
    "aviation_subtypes.communication",
    "aviation_subtypes.leadership_management",
    "aviation_subtypes.teamwork",
    "aviation_subtypes.problem_solving",
    "aviation_subtypes.decision_making",
    "aviation_subtypes.adaptability",
    "aviation_subtypes.time_management",
    "aviation_subtypes.conflict_resolution",
    "aviation_subtypes.networking",
    "aviation_subtypes.public_speaking",
    "aviation_subtypes.job_applications_interviews",
    "aviation_subtypes.emotional_intelligence",
    "aviation_subtypes.workplace_confidence",
    "aviation_subtypes.other",
  ],
};

/** The same seven sectors, for a member describing what they can teach. */
const SECONDARY_OFFERING = {
  "aviation_sectors.pilot_aeroplane": [
    "aviation_subtypes.any",
    "aviation_subtypes.student_pilot",
    "aviation_subtypes.newly_licensed_pilot",
    "aviation_subtypes.airline_pilot",
    "aviation_subtypes.corporate_business_aviation_pilot",
    "aviation_subtypes.charter_pilot",
    "aviation_subtypes.medevac_pilot",
    "aviation_subtypes.flight_instructor",
    "aviation_subtypes.aerial_work_pilot",
    "aviation_subtypes.government_pilot",
    "aviation_subtypes.military_pilot",
    "aviation_subtypes.test_pilot",
    "aviation_subtypes.other",
  ],
  "aviation_sectors.pilot_helicopter_rotary_wing": [
    "aviation_subtypes.any",
    "aviation_subtypes.student_pilot",
    "aviation_subtypes.newly_licensed_pilot",
    "aviation_subtypes.ems_medevac",
    "aviation_subtypes.utility_and_resource_operations",
    "aviation_subtypes.firefighting",
    "aviation_subtypes.tourism_and_charter",
    "aviation_subtypes.offshore_operations",
    "aviation_subtypes.military_pilot",
    "aviation_subtypes.flight_instructor",
    "aviation_subtypes.other",
  ],
  "aviation_sectors.air_traffic_services": [
    "aviation_subtypes.any",
    "aviation_subtypes.air_traffic_control",
    "aviation_subtypes.flight_service_specialist_fss",
    "aviation_subtypes.flight_information_services",
  ],
  "aviation_sectors.aircraft_maintenance": [
    "aviation_subtypes.any",
    "aviation_subtypes.avionics",
    "aviation_subtypes.structures",
    "aviation_subtypes.quality_assurance",
    "aviation_subtypes.maintenance",
  ],
  "aviation_sectors.data_analytics": [
    "aviation_subtypes.any",
    "aviation_subtypes.flight_data_and_performance",
    "aviation_subtypes.safety_and_risk_analysis",
    "aviation_subtypes.airport_operations_analytics",
    "aviation_subtypes.maintenance_analytics",
    "aviation_subtypes.weather_and_forecasting",
    "aviation_subtypes.business_intelligence_and_reporting",
  ],
  "aviation_sectors.rcaf": [
    "aviation_subtypes.any",
    "aviation_subtypes.military_pilot",
    "aviation_subtypes.aerospace_control",
    "aviation_subtypes.air_operations",
    "aviation_subtypes.aerospace_engineering",
    "aviation_subtypes.aircraft_maintenance",
    "aviation_subtypes.air_traffic_services",
    "aviation_subtypes.intelligence_and_surveillance",
    "aviation_subtypes.leadership_and_officer_careers",
  ],
  "aviation_sectors.professional_skills": [
    "aviation_subtypes.any",
    "aviation_subtypes.communication",
    "aviation_subtypes.leadership_management",
    "aviation_subtypes.teamwork",
    "aviation_subtypes.problem_solving",
    "aviation_subtypes.decision_making",
    "aviation_subtypes.adaptability",
    "aviation_subtypes.time_management",
    "aviation_subtypes.conflict_resolution",
    "aviation_subtypes.networking",
    "aviation_subtypes.public_speaking",
    "aviation_subtypes.job_applications_interviews",
    "aviation_subtypes.emotional_intelligence",
    "aviation_subtypes.workplace_confidence",
    "aviation_subtypes.other",
  ],
};

/**
 * Career stages a member can ask to learn FROM, on the Be-Mentored sub-flow.
 *
 * Optional and multi-select, with **empty meaning no preference** — which is
 * why there is no "no preference" member. The flow PDF lists one; the export
 * does not, the old flow had no career-stage field at all so nothing constrains
 * it, and `MentorProfile.genderPreference` already models the same idea as an
 * enum array where empty means no preference. An explicit option would also
 * allow the contradictory "no preference + early career".
 */
export const MENTOR_CAREER_STAGES = [
  "mentor_stages.early_career",
  "mentor_stages.mid_career_professional",
  "mentor_stages.senior_professional",
  "mentor_stages.executive_leadership",
];

/** Who a member wants to meet. Plural — these describe other people. */
const CONNECTION_TYPES = [
  "connection_types.students_and_new_entrants",
  "connection_types.industry_professionals",
  "connection_types.instructors_and_trainers",
  "connection_types.managers_and_leaders",
  "connection_types.executives",
  "connection_types.recruiters_and_hiring_managers",
  "connection_types.entrepreneurs_and_founders",
];

/**
 * How a member describes themselves. The same seven roles as
 * `CONNECTION_TYPES`, in the singular, because one is who you are and the other
 * is who you are looking for — a distinction that reads as a duplicate list
 * until you see them on screen together.
 *
 * Capped at three by `COMMUNITY_ROLE_MAX`, which the design enforces.
 */
const COMMUNITY_ROLES = [
  "community_roles.student_new_entrant",
  "community_roles.industry_professional",
  "community_roles.instructor_trainer",
  "community_roles.manager_leader",
  "community_roles.executive",
  "community_roles.recruiter_hiring_manager",
  "community_roles.entrepreneur_founder",
];

/** How many community roles a member may claim at once. */
export const COMMUNITY_ROLE_MAX = 3;

/** What a member wants their career to do next. */
const CAREER_GOALS = [
  "career_goals.explore_aviation_careers",
  "career_goals.earn_new_licenses_or_ratings",
  "career_goals.develop_leadership_skills",
  "career_goals.transition_to_a_new_aviation_role",
  "career_goals.advance_in_my_current_role",
  "career_goals.become_an_industry_leader",
  "career_goals.find_employment_opportunities",
  "career_goals.grow_my_industry_knowledge",
  "career_goals.start_or_grow_an_aviation_business",
];

/** How a member prefers to learn. Drives content recommendations. */
const LEARNING_FORMATS = [
  "learning_formats.interactive_courses",
  "learning_formats.webinars",
  "learning_formats.educational_videos",
  "learning_formats.articles_and_blogs",
  "learning_formats.presentations",
];

/**
 * Every non-sector option list, behind one accessor.
 *
 * Same seam as `getSectorTaxonomy` and for the same reason (decision 7): when
 * the backend owns these, this function body changes and nothing else does.
 * Keyed by the `asks` flag that turns each question on, so a screen reads
 * `getOptions("communityRole")` without knowing where the list lives.
 */
export const getOptions = (question) =>
  ({
    connectionTypes: CONNECTION_TYPES,
    communityRole: COMMUNITY_ROLES,
    careerGoals: CAREER_GOALS,
    learningFormats: LEARNING_FORMATS,
    experienceStage: MENTOR_CAREER_STAGES,
    careerStage: MENTOR_CAREER_STAGES,
  })[question] ?? [];

/**
 * The single seam the sub-flows use.
 *
 * Returns ids only. Shaped like the `GET /v1/web/onboarding/taxonomy` response
 * the MSW layer serves, so swapping the hardcoded lists for a fetch is a change
 * to this function body and nothing else.
 *
 * @param {"seeking"|"offering"} [mode]
 * @returns {{ sectors: string[], secondary: Record<string, string[]> }}
 */
export const getSectorTaxonomy = (mode = "seeking") => ({
  sectors: AVIATION_SECTORS,
  secondary: mode === "offering" ? SECONDARY_OFFERING : SECONDARY_SEEKING,
});

/**
 * Screens presented by the goal sub-flows.
 *
 * Each selected goal contributes its own run of screens. Every sub-flow in the
 * design is a single screen — one component, one Continue, no internal step
 * state — so each goal counts once and `personFlowWeight` is simply the number
 * of goals selected.
 *
 * This is what decides how far the bar moves *within* the person-flow step. It
 * is NOT the progress denominator: that counts steps and is fixed once the
 * pathway is known, which is why choosing more goals lengthens the sub-flow
 * without moving the total.
 *
 * Keys must stay in step with `SUB_FLOW_ORDER`. `personFlowWeight` sums this
 * map over the raw goal array while `planSubFlows` filters by that list, so a
 * goal present in one and absent from the other reserves a screen that is never
 * presented and the bar can never fill. `sanitizeFlowState` stops an unknown id
 * reaching either, which is what makes the `?? 1` fallback below a backstop
 * rather than something load-bearing.
 */
const SUB_FLOW_SCREENS = {
  receive_mentorship: 1,
  provide_mentorship: 1,
  networking: 1,
  grow_career: 1,
  build_skills: 1,
};

/**
 * Sub-flows run in this order, no matter the order the goals were selected in.
 */
export const SUB_FLOW_ORDER = [
  "receive_mentorship",
  "provide_mentorship",
  "networking",
  "grow_career",
  "build_skills",
];

/**
 * Total screens the person-flow step will present for the selected goals.
 * At least one, so the step is never weightless while it is still reachable.
 */
export const personFlowWeight = (state) => {
  const total = (state.goals ?? []).reduce(
    (sum, goal) => sum + (SUB_FLOW_SCREENS[goal] ?? 1),
    0,
  );
  return Math.max(total, 1);
};

/** City and province: the parts of a location the server requires. */
export const addressIsComplete = (address) =>
  Boolean(address?.city?.trim?.() && address?.province?.trim?.());

export const profileIsComplete = (state) =>
  Boolean(
    state.profile?.firstName?.trim?.() &&
    state.profile?.lastName?.trim?.() &&
    state.profile?.mainPhoto?.url &&
    addressIsComplete(state.profile?.address),
  );

/** Optional employment entries must be complete once the member starts them. */
export const profileIsValid = (state) => {
  const profile = state.profile;
  const title = profile?.currentRole?.title ?? profile?.role ?? "";
  const organization = profile?.currentRole?.organization ?? "";
  return (
    profileIsComplete(state) &&
    Boolean(title.trim()) === Boolean(organization.trim()) &&
    (profile.pastRoles ?? []).every(
      (role) => role.title?.trim() && role.organization?.trim(),
    )
  );
};

/** Goals chosen and, for a person, their sub-flows walked. */
const profileIsReachable = (s) =>
  (s.goals ?? []).length > 0 &&
  (s.pathway === "organization" || subFlowsComplete(s));

/**
 * The profile's own precondition as well as its fields: a stored flow whose
 * goals are cleared on load keeps its profile.
 */
const pastProfile = (s) => profileIsReachable(s) && profileIsValid(s);

// Registration saves DOB on the server. Account recovery must not depend on a
// copy surviving in this tab; sponsor-only accounts are exempt server-side.
const dobGateSatisfied = (s) => s.existingAccount === true;

/** @type {StepDefinition[]} */
export const STEPS = [
  {
    id: "welcome",
    path: "welcome",
    weight: ONE_SCREEN,
    appliesTo: () => true,
    isReachable: () => true,
    ticket: "ECH-353",
  },
  {
    id: "dob",
    path: "date-of-birth",
    weight: ONE_SCREEN,
    // Before a pathway is chosen the individual one is assumed, because it is
    // the only one that can be selected today. Assuming otherwise would show a
    // total that drops the moment someone picks the obvious option.
    appliesTo: (s) => s.pathway !== "organization",
    // Only the individual pathway collects a date of birth.
    isReachable: (s) => s.pathway === "person",
    ticket: "ECH-353",
  },
  {
    id: "create_account",
    path: "create-account",
    weight: ONE_SCREEN,
    appliesTo: (s) => s.existingAccount !== true,
    // The age gate is enforced here, not only on the DOB screen: an individual
    // cannot reach account creation without a date of birth that passed.
    //
    // Organizations have no age gate and never collect a DOB, so requiring one
    // unconditionally would make the corporate pathway unreachable — the
    // pathway is ECH-321's to build, but the registry has to model it
    // correctly or that ticket starts from a broken flow.
    isReachable: (s) =>
      s.existingAccount !== true &&
      (s.pathway === "organization" || Boolean(s.dob)),
    ticket: "ECH-350",
  },
  {
    id: "verify_email",
    path: "verify-email",
    weight: ONE_SCREEN,
    appliesTo: (s) => s.existingAccount !== true || !s.emailVerified,
    // Keep verified email in the reachable prefix so the guard can advance past
    // this completed step after an OTP submission or a persisted refresh.
    isReachable: (s) => Boolean(s.email),
    ticket: "ECH-350",
  },
  {
    id: "goals",
    path: "goals",
    weight: ONE_SCREEN,
    appliesTo: () => true,
    // DOB belongs to registration. A resumed account can reach goals once its
    // email is verified, even when no local DOB survived.
    isReachable: (s) => s.emailVerified === true,
    ticket: "ECH-116",
  },
  {
    id: "person_flow",
    path: "about-you",
    weight: personFlowWeight,
    appliesTo: (s) => s.pathway !== "organization",
    isReachable: (s) => (s.goals ?? []).length > 0,
    ticket: "ECH-116",
  },
  {
    id: "profile",
    path: "profile",
    weight: ONE_SCREEN,
    appliesTo: () => true,
    // Deliberately not `person_flow`'s precondition. Sharing it would mean the
    // goal step is the only gate, so one URL forward from it skips every
    // sub-flow — the guard would allow it, because the goals *were* chosen.
    // The sub-flows have to have been walked; see `subFlowsComplete`.
    //
    // Organizations are exempt because `person_flow` does not apply to them, so
    // they would carry a plan they never walk and profile would be permanently
    // unreachable. Same shape as the age gate on `create_account` above.
    isReachable: profileIsReachable,
    ticket: "ECH-354",
  },
  {
    id: "demographics",
    path: "demographics",
    weight: ONE_SCREEN,
    appliesTo: () => true,
    isReachable: pastProfile,
    ticket: "ECH-354",
  },
  {
    id: "done",
    path: "done",
    weight: ONE_SCREEN,
    appliesTo: () => true,
    // A post-submission confirmation, not a step the member completes, so the
    // person flow counts eight and the last numbered step is demographics.
    countsTowardProgress: false,
    // Demographics is skippable, so completion depends on the profile, not on
    // demographics having been answered.
    isReachable: pastProfile,
    // Arriving sends completion, so the flow never resumes onto this step and
    // it offers no way back.
    submitsOnArrival: true,
    ticket: "ECH-354",
  },
];

/**
 * There is deliberately no `plan` step. Individual purchasing is paused until
 * the Paid tier module ships; ECH-352 adds it back. See
 * docs/onboarding-redesign-decisions.md, decision 1.
 */

export const STEP_IDS = STEPS.map((s) => s.id);

/** Every onboarding URL lives under this. */
export const ONBOARDING_BASE_PATH = "/onboarding";

export const getStep = (id) => STEPS.find((s) => s.id === id) ?? null;

export const getStepByPath = (path) =>
  STEPS.find((s) => s.path === path) ?? null;

/**
 * The step a URL belongs to.
 *
 * Matches the first segment after the base path, NOT the last segment of the
 * URL. Steps are allowed to route internally — the goal sub-flows in ECH-116
 * will live at paths like `/onboarding/about-you/networking` — and reading the
 * last segment would fail to identify the step the moment that happens. The
 * chrome would silently vanish and progress would report `unknown`, on the one
 * step where the bar matters most.
 */
export const getStepByLocation = (pathname) => {
  const segments = pathname.split("/").filter(Boolean);
  const baseSegments = ONBOARDING_BASE_PATH.split("/").filter(Boolean);

  const isUnderBase = baseSegments.every((seg, i) => segments[i] === seg);
  if (!isUnderBase) return null;

  return getStepByPath(segments[baseSegments.length]);
};

export const stepIndex = (id) => STEPS.findIndex((s) => s.id === id);

/** Resolve a step's weight, which may be a function of flow state. */
export const weightOf = (step, state) =>
  typeof step.weight === "function" ? step.weight(state) : step.weight;

const isStepIncluded = (step, state) =>
  !(step.id === "dob" && dobGateSatisfied(state));

/**
 * The numbered steps this journey counts, whether or not they can be entered
 * yet.
 *
 * This is the progress denominator, and it is deliberately NOT `reachableSteps`.
 * Reachability answers "may the flow enter this step now", which is false for
 * everything ahead of the member — so using it as a denominator meant the total
 * grew as they answered questions. The bar opened at "1 of 2" and stretched all
 * the way down, which reads as the finish line moving away from you.
 *
 * Membership depends only on which pathway is being taken, so the total is
 * known from the first screen and does not move.
 */
export const applicableSteps = (state) =>
  STEPS.filter(
    (s) =>
      (s.appliesTo ? s.appliesTo(state) : true) &&
      isStepIncluded(s, state) &&
      s.countsTowardProgress !== false,
  );

/**
 * The steps the flow may currently enter. Used by the guard and by navigation,
 * never as a progress denominator — see `applicableSteps`.
 */
export const reachableSteps = (state) =>
  STEPS.filter((s) => s.isReachable(state) && isStepIncluded(s, state));

/**
 * The furthest step the flow may currently enter.
 *
 * Walks forward from the start and stops at the first step whose precondition
 * is unmet. Used by the route guard: deep-linking past this point redirects
 * here rather than rendering a screen with nothing behind it.
 */
export const furthestReachableStep = (state) => {
  let furthest = STEPS[0];
  for (const step of STEPS) {
    if (step.appliesTo && !step.appliesTo(state)) continue;
    if (isStepSatisfied(step, state)) continue;
    // Entered only by a deliberate Continue, never by a resume or a redirect.
    if (step.submitsOnArrival) break;
    if (!step.isReachable(state)) break;
    furthest = step;
  }
  return furthest;
};

/**
 * The step that sends the flow, when the state may enter it.
 *
 * Null when it may not, which is what keeps "completion is sent, so go back to
 * the confirmation" from bouncing against the redirect that answers an
 * unreachable step.
 */
export const submittingStep = (state) =>
  STEPS.find((s) => s.submitsOnArrival && s.isReachable(state)) ?? null;

/** The next step after `id` that is reachable in the current state. */
export const nextStep = (id, state) => {
  const from = stepIndex(id);
  if (from === -1) return null;
  return (
    STEPS.slice(from + 1).find(
      (s) => s.isReachable(state) && !isStepSatisfied(s, state),
    ) ?? null
  );
};

/**
 * Whether a step's work is already complete and should not be entered again.
 *
 * Reachability stays permissive so the forward walk can move past completed
 * registration steps. Entry and back-navigation need the narrower question:
 * would opening this screen ask the member to repeat work they already did?
 */
export const isStepSatisfied = (step, state) => {
  // Verification commits the account details collected through this point.
  // Closing the whole prefix prevents browser history or a typed URL from
  // returning the member to a screen that can no longer be completed safely.
  if (
    state.emailVerified === true &&
    ["welcome", "create_account", "verify_email"].includes(step?.id)
  ) {
    return true;
  }
  // A newly registered account has already passed this screen before OTP.
  if (step?.id === "dob" && state.emailVerified && !state.existingAccount) {
    return true;
  }
  if (step?.id === "dob") return dobGateSatisfied(state);
  return false;
};

/** The step whose sub-flows are the goal screens. */
export const SUB_FLOW_STEP_ID = "person_flow";

/**
 * Where the back control goes from here.
 *
 * The layout's back button is step-based, so from any goal sub-flow it would
 * jump straight out to `goals`, skipping every sub-flow already completed. The
 * fix belongs here rather than in the layout: this module is the single source
 * of truth for sequence, and the layout should stay ignorant of what a goal is.
 *
 * A second back control inside the sub-flow is deliberately NOT the answer —
 * two controls doing the same thing is one to keep in sync and one for a screen
 * reader to announce twice (see `StepPlaceholder`).
 *
 * @param {StepDefinition} step
 * @param {FlowState} state
 * @param {string} [pathname] the current URL, so callers need not know that a
 *   goal is what distinguishes one sub-flow from another
 * @returns {{path: string}|null} null when there is nowhere back
 */
export const backLocation = (step, state, pathname = "") => {
  // Completion is already sent; there is nothing left to go back and change.
  if (step?.submitsOnArrival) return null;

  if (step?.id === SUB_FLOW_STEP_ID) {
    const params = planSubFlows(state.goals).map((s) => s.param);
    const index = params.indexOf(subFlowParamFromPath(pathname));

    // Anywhere but the first sub-flow steps back one goal rather than leaving.
    if (index > 0) return { path: `${step.path}/${params[index - 1]}` };
  }

  const previous = previousStep(step?.id, state);
  return previous ? { path: previous.path } : null;
};

/**
 * The sub-flow segment of a URL: `/onboarding/about-you/networking` ->
 * `networking`.
 *
 * Null on the bare step path, which is the index redirect rather than a
 * sub-flow. Reading the segment by position rather than taking the last one
 * keeps those two cases apart — `.pop()` would report `about-you` as if it
 * were a goal.
 */
export const subFlowParamFromPath = (pathname = "") => {
  const segments = pathname.split("/").filter(Boolean);
  const baseLength = ONBOARDING_BASE_PATH.split("/").filter(Boolean).length;
  return segments[baseLength + 1] ?? null;
};

/** URL form of a goal id: `receive_mentorship` -> `receive-mentorship`. */
export const goalToParam = (goal) => goal.replace(/_/g, "-");

/** Inverse of `goalToParam`; null for anything that is not a known goal. */
export const paramToGoal = (param) =>
  SUB_FLOW_ORDER.find((goal) => goalToParam(goal) === param) ?? null;

/** The previous reachable step before `id`. */
export const previousStep = (id, state) => {
  const from = stepIndex(id);
  if (from <= 0) return null;
  return (
    STEPS.slice(0, from)
      .reverse()
      .find((s) => s.isReachable(state) && !isStepSatisfied(s, state)) ?? null
  );
};

/** Goals that ask the seeking sector question; the first one selected asks it. */
const SEEKING_SECTOR_PRIORITY = [
  "receive_mentorship",
  "grow_career",
  "networking",
  "build_skills",
];

/**
 * Which sub-flows a member walks, in `SUB_FLOW_ORDER`, and what each one asks.
 *
 * A question several goals share is asked once, by the first selected goal in
 * its list. `provide_mentorship` asks sectors in the offering direction, which
 * answers none of them.
 *
 *   sectors (seeking)  receive_mentorship, grow_career, networking, build_skills
 *   communityRole      grow_career, networking
 *   connectionTypes    networking, grow_career
 *   experienceStage    provide_mentorship, grow_career
 *
 *   careerStage        receive_mentorship only
 *   careerGoals        grow_career only
 *   learningFormats    build_skills only
 *
 * @param {string[]} goals  selected goal ids, in any order
 * @returns {{goal: string, param: string, asks: object}[]} ordered sub-flows
 */
export const planSubFlows = (goals = []) => {
  const has = (goal) => goals.includes(goal);

  const seekingOwner = SEEKING_SECTOR_PRIORITY.find(has) ?? null;
  const seekingSectors = (goal) => (goal === seekingOwner ? "seeking" : false);

  const asks = {
    receive_mentorship: {
      sectors: seekingSectors("receive_mentorship"),
      careerStage: true, // the stages they want to learn FROM (multi, optional)
    },
    provide_mentorship: {
      sectors: "offering",
      experienceStage: true, // their OWN stage (single, required)
    },
    networking: {
      sectors: seekingSectors("networking"),
      communityRole: !has("grow_career"),
      connectionTypes: true,
    },
    grow_career: {
      careerGoals: true,
      communityRole: true,
      experienceStage: !has("provide_mentorship"),
      sectors: seekingSectors("grow_career"),
      connectionTypes: !has("networking"),
    },
    build_skills: {
      // The goal holding the seeking sectors, or null when skills asks for them itself.
      recommendFrom: seekingOwner === "build_skills" ? null : seekingOwner,
      sectors: seekingSectors("build_skills"),
      learningFormats: true,
    },
  };

  return SUB_FLOW_ORDER.filter((goal) => has(goal)).map((goal) => ({
    goal,
    param: goalToParam(goal),
    asks: asks[goal],
  }));
};

/**
 * How far into the sub-flows the member may currently go.
 *
 * The step guards deep-linking with `furthestReachableStep`, which walks the
 * registry and stops at the first unmet precondition. Sub-flows need the same
 * protection or a member who selected five goals can type
 * `/about-you/build-skills` and skip the four in between — the guard would let
 * them through, because the goal *is* one they chose.
 *
 * A sub-flow counts as done once it has an entry in `goalData`. Presence is the
 * signal, never content: what a sub-flow collects depends on the dedup rules,
 * so a check that inspected the answers would have to know those rules too.
 *
 * @returns {number} index of the furthest sub-flow that may be entered
 */
export const furthestSubFlowIndex = (goals, goalData = {}) => {
  const plan = planSubFlows(goals);

  for (let i = 0; i < plan.length; i++) {
    if (goalData[plan[i].goal] === undefined) return i;
  }
  // Everything answered: the last one stays open so it can be revisited.
  return Math.max(plan.length - 1, 0);
};

/**
 * Whether every planned sub-flow has been walked.
 *
 * `furthestSubFlowIndex`'s predicate quantified over the whole plan instead of
 * scanned for the first gap, so the guard admitting a member to `profile` and
 * the sequencer moving them through the sub-flows cannot disagree about what
 * "done" means.
 */
export const subFlowsComplete = (state) =>
  planSubFlows(state.goals).every(
    (s) => state.goalData?.[s.goal] !== undefined,
  );

/** Position of a goal within the planned sequence; -1 when it is not in it. */
export const subFlowIndex = (goals, goal) =>
  planSubFlows(goals).findIndex((s) => s.goal === goal);

/**
 * How many of a step's own screens are already behind the member.
 *
 * The complement of `weightOf`: that says how many screens a step presents,
 * this says how far into them a given URL is.
 *
 * Derived, never stored. `Router.jsx` keys `<Routes>` on the pathname, so every
 * navigation unmounts the whole tree and a stored count has nowhere to survive;
 * refilling one from an effect after the remount paints the step's base for a
 * commit before jumping forward, which reads as the bar moving backwards while
 * the member moves forwards.
 *
 * Single-screen steps are always 0: there is nothing behind the member inside a
 * step that presents one screen.
 */
export const screensDoneIn = (step, state, pathname = "") => {
  if (step?.id !== SUB_FLOW_STEP_ID) return 0;

  const goal = paramToGoal(subFlowParamFromPath(pathname));
  return Math.max(subFlowIndex(state.goals ?? [], goal), 0);
};
